import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  buildRecommendationQuery,
  buildSellerListingsQuery,
  getAuthorId,
  rankFitMatches,
  rankSellerListings,
} from '../../util/recommendedListings';
import {
  apiBaseUrl,
  postCreateReserveListing,
  postDeleteReserveListing,
  transactionLineItems,
  postcomment,
  getSavedQueries,
  updateTxSearchData,
  updateTxMetadata,
  recordListingView,
  postSellerPaymentStatus,
} from '../../util/api';
import { getSessionId } from '../../util/searchKeywordTracker';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { isJunkTitle } from '../../util/listingTitle';
import { isStaleListing } from '../SearchPage/resultCardData';
import { MARKETPLACE_FILTER } from '../../util/catalogueCore';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const GET_SAVED_QUERIES_REQUEST = 'app/ListingPage/GET_SAVED_QUERIES_REQUEST';
export const GET_SAVED_QUERIES_SUCCESS = 'app/ListingPage/GET_SAVED_QUERIES_SUCCESS';
export const GET_SAVED_QUERIES_ERROR = 'app/ListingPage/GET_SAVED_QUERIES_ERROR';

export const FETCH_RECOMMENDED_LISTINGS_REQUEST = 'app/ListingPage/FETCH_RECOMMENDED_LISTINGS_REQUEST';
export const FETCH_RECOMMENDED_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_RECOMMENDED_LISTINGS_SUCCESS';

export const FETCH_LISTING_FITMENT_REQUEST = 'app/ListingPage/FETCH_LISTING_FITMENT_REQUEST';
export const FETCH_LISTING_FITMENT_SUCCESS = 'app/ListingPage/FETCH_LISTING_FITMENT_SUCCESS';

export const FETCH_AUTHOR_PAYMENT_STATUS_SUCCESS =
  'app/ListingPage/FETCH_AUTHOR_PAYMENT_STATUS_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
   getSavedQueriesInProgress: false,
  getSavedQueriesError: null,
  savedQueries: [],
  // Recommended listings are loaded in loadData so they render server-side
  // (crawlable). They are kept in this slice — NOT in the shared marketplace
  // entity store — to avoid overwriting the current listing's image variants.
  recommendedListings: [],
  // The seller's other active listings, shown in the second row.
  sellerListings: [],
  // Which listing the two rows above were built for. The section skips its
  // own fetch when this already matches the listing on screen, so a normal
  // page load asks the API once instead of twice.
  recommendedForListingId: null,
  /* "Passar även" (P25009-409 AC-2). This used to be fetched by the section
   * itself in a useEffect, which meant it existed only after JavaScript ran —
   * and an AI crawler does not run JavaScript, so the list of cars a wheel fits
   * was invisible to exactly the reader it was written for. It is loaded in
   * loadData now, so it is in the HTML the server delivers. */
  fitsAlsoCars: [],
  fitsAlsoTotal: 0,
  fitsAlsoBasis: 'none',
  fitsAlsoForListingId: null,
  /* P25009-452 BR-1 — whether the listing's author can take payment
   * (connected Stripe account OR own payment system). Three-valued on
   * purpose: `null` means "not known (yet)" and the page fails OPEN — the buy
   * button only changes to "Kontakta säljaren" on an explicit `false`, so a
   * hiccup in the status endpoint never blocks a payable seller's sales. */
  authorCanTakePayment: null,
  /* Whether that payment happens in the seller's own system (P25009-476): the
   * buyer still orders here, but pays through a link the seller sends. Only an
   * explicit `true` changes the page; `null` is "not known". */
  authorUsesOwnPayment: null,
  authorPaymentStatusForAuthorId: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_RECOMMENDED_LISTINGS_REQUEST:
      return {
        ...state,
        recommendedListings: [],
        sellerListings: [],
        recommendedForListingId: null,
      };
    case FETCH_RECOMMENDED_LISTINGS_SUCCESS:
      return {
        ...state,
        recommendedListings: payload.listings,
        sellerListings: payload.sellerListings,
        recommendedForListingId: payload.listingId,
      };

    case FETCH_LISTING_FITMENT_REQUEST:
      return {
        ...state,
        fitsAlsoCars: [],
        fitsAlsoTotal: 0,
        fitsAlsoBasis: 'none',
        fitsAlsoForListingId: null,
      };
    case FETCH_LISTING_FITMENT_SUCCESS:
      return {
        ...state,
        fitsAlsoCars: payload.cars,
        fitsAlsoTotal: payload.total,
        fitsAlsoBasis: payload.basis,
        fitsAlsoForListingId: payload.listingId,
      };

    case FETCH_AUTHOR_PAYMENT_STATUS_SUCCESS:
      return {
        ...state,
        authorCanTakePayment: payload.canTakePayment,
        authorUsesOwnPayment: payload.useOwnPayment,
        authorPaymentStatusForAuthorId: payload.authorId,
      };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

       case GET_SAVED_QUERIES_REQUEST:
            return {
              ...state,
              getSavedQueriesInProgress: true,
              getSavedQueriesError: null,
            };
      
          case GET_SAVED_QUERIES_SUCCESS:
            return {
              ...state,
              getSavedQueriesInProgress: false,
              savedQueries: payload,
            };
      
          case GET_SAVED_QUERIES_ERROR:
            return {
              ...state,
              getSavedQueriesInProgress: false,
              getSavedQueriesError: payload,
            };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });

export const fetchRecommendedListingsRequest = () => ({
  type: FETCH_RECOMMENDED_LISTINGS_REQUEST,
});
export const fetchRecommendedListingsSuccess = (listings, sellerListings, listingId) => ({
  type: FETCH_RECOMMENDED_LISTINGS_SUCCESS,
  payload: { listings, sellerListings, listingId },
});
export const fetchListingFitmentRequest = () => ({ type: FETCH_LISTING_FITMENT_REQUEST });
export const fetchListingFitmentSuccess = ({ cars, total, basis, listingId }) => ({
  type: FETCH_LISTING_FITMENT_SUCCESS,
  payload: { cars, total, basis, listingId },
});
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const getSavedQueriesRequest = () => ({ type: GET_SAVED_QUERIES_REQUEST });
export const getSavedQueriesSuccess = savedQueries => ({ type: GET_SAVED_QUERIES_SUCCESS, payload: savedQueries });
export const getSavedQueriesError = e => ({ type: GET_SAVED_QUERIES_ERROR, error: true, payload: e });

// ================ Thunks ================ //

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

/* P25009-452 BR-1 — ask the server whether this listing's author can take
 * payment. Called from the page (a useEffect, so it never runs during SSR —
 * util/api reads `window`). Fails OPEN: on any endpoint error the status is
 * recorded as `null`/unknown and the buy button stays, because the checkout's
 * own guard still catches the rare unpayable seller. */
export const fetchAuthorPaymentStatus = authorId => (dispatch, getState) => {
  const state = getState().ListingPage;
  if (!authorId || state.authorPaymentStatusForAuthorId === authorId) {
    return Promise.resolve();
  }
  return postSellerPaymentStatus({ authorId })
    .then(res => {
      dispatch({
        type: FETCH_AUTHOR_PAYMENT_STATUS_SUCCESS,
        payload: {
          authorId,
          canTakePayment: res?.canTakePayment !== false,
          useOwnPayment: typeof res?.useOwnPayment === 'boolean' ? res.useOwnPayment : null,
        },
      });
    })
    .catch(e => {
      log.error(storableError(e), 'fetch-author-payment-status-failed', { authorId });
      dispatch({
        type: FETCH_AUTHOR_PAYMENT_STATUS_SUCCESS,
        payload: { authorId, canTakePayment: null, useOwnPayment: null },
      });
    });
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

// Loads the two rows under a listing:
//   1. the fit row   — listings that fit the one on screen
//   2. the seller row — the seller's other active listings
//
// The queries only narrow; util/recommendedListings.js re-checks every
// candidate before it is kept, because the API silently ignores a pub_*
// filter it has no index for. See that module for the rules.
//
// Returns denormalised listings to the caller instead of writing to the
// marketplace entity store. Going through addMarketplaceEntities would
// overwrite the current listing's image entity (these queries request
// fewer image variants than ListingPage.showListing), breaking the main
// image gallery.
export const fetchRecommendedListings = (listing, config) => (dispatch, getState, sdk) => {
  dispatch(fetchRecommendedListingsRequest());

  const currentId = listing?.id?.uuid;
  const empty = { listings: [], sellerListings: [] };
  if (!currentId) {
    dispatch(fetchRecommendedListingsSuccess([], [], null));
    return Promise.resolve(empty);
  }

  const fitParams = buildRecommendationQuery(listing);
  const sellerParams = buildSellerListingsQuery(getAuthorId(listing));
  if (!fitParams && !sellerParams) {
    dispatch(fetchRecommendedListingsSuccess([], [], currentId));
    return Promise.resolve(empty);
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const imageParams = {
    // currentStock: recommendations show only what is in stock (P25009-486 BR-6).
    include: ['images', 'author', 'author.profileImage', 'currentStock'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  // One row failing must not take the other down with it, so each query
  // resolves to an empty pool of its own rather than rejecting.
  const runQuery = (params, label) =>
    params
      ? sdk.listings
          .query({ ...params, ...imageParams })
          .then(denormalisedResponseEntities)
          .catch(e => {
            log.error(e, label, { listingId: currentId });
            return [];
          })
      : Promise.resolve([]);

  /* P25009-486 BR-6 — a recommendation is something the buyer can actually
   * buy: in stock, not deleted, hidden or reserved, touched in the last 12
   * months, and with a real title (never "RÖR EJ!!!"). The same stock filter
   * the search page uses. */
  /* P25009-410 — and on the marketplace: a recommendation is an ad, never a
   * row from the seller's catalogue. */
  const stockFilter = {
    minStock: 1,
    stockMode: 'match-undefined',
    pub_isDeleted: false,
    ...MARKETPLACE_FILTER,
  };
  const isRecommendable = l => {
    const publicData = l?.attributes?.publicData || {};
    return (
      !publicData.isHide &&
      !publicData.isReserved &&
      !isJunkTitle(l?.attributes?.title) &&
      !isStaleListing(l)
    );
  };

  return Promise.all([
    runQuery(fitParams ? { ...fitParams, ...stockFilter } : null, 'fetch-recommended-listings-failed'),
    runQuery(
      sellerParams ? { ...sellerParams, ...MARKETPLACE_FILTER } : null,
      'fetch-seller-listings-failed'
    ),
  ]).then(([fitPool, sellerPool]) => {
    const listings = rankFitMatches(listing, fitPool.filter(isRecommendable));
    const sellerListings = rankSellerListings(listing, sellerPool);
    dispatch(fetchRecommendedListingsSuccess(listings, sellerListings, currentId));
    return { listings, sellerListings };
  });
};

/* Which cars this wheel also fits (P25009-409 AC-2, P25009-398).
 *
 * Loaded here rather than in the section component so the list is in the HTML
 * the server delivers. Our own API is reached with a bare `fetch` and an
 * absolute URL, the same way SeoPages.duck.js does it: the `post()` helper in
 * util/api.js calls `window.fetch`, which does not exist during server-side
 * rendering.
 *
 * Never rejects. A listing page must render whether or not this works, and no
 * list is the right outcome for a failure — better than half an answer
 * presented as a guarantee of fit.
 */
export const fetchFitsAlsoCars = (listing, config) => dispatch => {
  const listingId = listing?.id?.uuid;
  const adType = listing?.attributes?.publicData?.adType;
  const empty = { cars: [], total: 0, basis: 'none' };

  dispatch(fetchListingFitmentRequest());

  /* Accessories carry no fitment, and tyres get no list until load and speed
   * index can be checked (P25009-473); asking would spend a request to learn
   * nothing. */
  if (!listingId || !['rim', 'wheel'].includes(adType)) {
    dispatch(fetchListingFitmentSuccess({ ...empty, listingId: listingId || null }));
    return Promise.resolve(empty);
  }

  const url = `${apiBaseUrl(config?.marketplaceRootURL)}/api/listing-fitment`;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ listingId }),
  })
    .then(res => {
      if (!res.ok) {
        throw new Error(`listing-fitment fetch failed with status ${res.status}`);
      }
      return res.json();
    })
    .then(body => {
      const result = {
        cars: body?.cars || [],
        total: body?.total || 0,
        basis: body?.basis || 'none',
      };
      dispatch(fetchListingFitmentSuccess({ ...result, listingId }));
      return result;
    })
    .catch(e => {
      log.error(e, 'fetch-listing-fitment-failed', { listingId });
      dispatch(fetchListingFitmentSuccess({ ...empty, listingId }));
      return empty;
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(async response => {
      const transactionId = response.data.data.id;
      const messageDate = new Date().getTime();

      // Step 1: await searchData metadata update
      const customerDisplayName = getState()?.user?.currentUser?.attributes?.profile?.displayName || '';
      if (transactionId?.uuid && listingId?.uuid) {
        try {
          await updateTxSearchData({
            transactionId: transactionId.uuid,
            listingId: listingId.uuid,
            customerDisplayName,
          });
          console.log('searchData updated successfully for inquiry:', transactionId.uuid);
        } catch (e) {
          console.error('Failed to update searchData metadata (inquiry):', e);
        }
      }

      // Step 2: await regular metadata update (lastMessageDate etc.)
      try {
        await updateTxMetadata({
          id: transactionId.uuid,
          metadata: {
            lastMessageDate: messageDate,
            providerLastMessageDate: messageDate,
            inquiryMessageDate: messageDate,
            Messaged: true,
            providerHasUnreadMessages: true,
          },
        });
      } catch (e) {
        console.error('Failed to update transaction metadata:', e);
      }

      // Step 3: Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const getUserSavedQueries = (params) => (dispatch, getState, sdk) => {
  dispatch(getSavedQueriesRequest());
  return getSavedQueries({})
    .then((res) => {
      // console.log('res',res)
      dispatch(getSavedQueriesSuccess(res.savedQueries));
      return res.savedQueries;
    })
    .catch((e) => dispatch(getSavedQueriesError(e)));
}

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  return Promise.all([
    dispatch(showListing(listingId, config)),
    dispatch(fetchReviews(listingId)),
  ]).then(response => {
    // showListing swallows fetch errors in its own .catch and resolves to
    // undefined. Guard here so a failed listing load doesn't throw
    // ("Cannot read properties of undefined") and cascade into a generic
    // load-data-failed — the listing error is already in state for the UI.
    const listing = response?.[0]?.data?.data;
    if (!listing) {
      return response;
    }
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';

    if (isBookingProcessAlias(transactionProcessAlias)) {
      // Fetch timeSlots.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    /* Load the recommended listings and the "Passar även" car list as part of
     * loadData so both are present in the server-rendered HTML (crawlable
     * links, crawlable car names). Both thunks swallow their own errors and
     * never reject, so neither can break the page load.
     *
     * P25009-498 — "Passar även" is hidden on the listing page for now, so its
     * fetch is off too: nothing reads it, and it held up every server render
     * of a rim or wheel listing. Uncomment together with the section. */
    return Promise.all([
      dispatch(fetchRecommendedListings(listing, config)),
      // dispatch(fetchFitsAlsoCars(listing, config)),
    ]).then(() => response);
  });
};

export const updateListingViews = (listing) => (dispatch) => {
  /* One recorded view: the server logs the event (P25009-385 BR-1) and adds
   * one to the listing's running total. Fire-and-forget: a view counter must
   * never delay or break a listing page, so the promise is deliberately not
   * awaited and its failure is swallowed.
   *
   * This used to also write publicData.totalViews + 1 through
   * /api/update-listing. That write is owner-only since 2026-09-03, so every
   * visitor's bump came back 401/403 as an uncaught error and the lifetime
   * figure froze. The counter is now the historic total, and the seller's pages
   * add the running total to it (src/util/listingViews.js). A Sharetribe write
   * per page view would also cost 10 API credits a view. */
  const authorId = listing?.author?.id?.uuid;
  if (listing?.id?.uuid && authorId) {
    recordListingView({
      listingId: listing.id.uuid,
      authorId,
      sessionId: getSessionId(),
    }).catch(() => {});
  }
}

export const reserveListing = (params) => async dispatch => {
  try {
    await postCreateReserveListing(params)
    window.location.reload()
  } catch (err) {
    console.log(err)
    throw "Failed"
  }
}

export const releaseListing = (params) => async dispatch => {
  try {
    await postDeleteReserveListing(params)
    window.location.reload()
  } catch (err) {
    console.log(err)
    throw "Failed"
  }
}
export const postNewComent = (params) => async dispatch => {
  try {
    await postcomment(params)
    window.location.reload()
  } catch (err) {
    console.log(err)
    throw "Failed"
  }
}


